import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { deleteTestRun } from '../../api/controlTestRuns.js';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { isModuleAccessDeniedError, MODULE_ACCESS_DENIED_USER_MESSAGE } from '../../request/moduleAccessDenied.ts';
import { queryKeys } from '../../queryKeys.ts';

export interface DeleteRunsResult {
  /** Runs that could not be deleted (empty when everything went). */
  remaining: string[];
}

const isNotFound = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404;

/** Module access denied — as the backend sends it, or after the axios interceptor rewrote it. */
const isAccessDenied = (err: unknown): boolean => {
  if (isModuleAccessDeniedError(err)) return true;
  const res = (err as { response?: { status?: number; data?: { error?: unknown } } })?.response;
  return res?.status === 403 && res.data?.error === MODULE_ACCESS_DENIED_USER_MESSAGE;
};

/** The id the axios interceptor gives its own toast for this error (it toasts `detail ?? message`). */
const interceptorToastId = (err: unknown): string | undefined => {
  const data = (err as { response?: { data?: { detail?: unknown; message?: unknown } } })?.response?.data;
  const msg = data?.detail ?? data?.message;
  return typeof msg === 'string' && msg ? msg : undefined;
};

/**
 * Delete each run, independently: a run that is already gone (404) counts as deleted, so retrying
 * after a partial failure only targets what is left.
 */
export async function deleteRuns(runIds: string[]): Promise<DeleteRunsResult & { firstError: unknown }> {
  const results = await Promise.allSettled(runIds.map((id) => deleteTestRun(id)));
  const remaining: string[] = [];
  let firstError: unknown = null;
  results.forEach((r, i) => {
    if (r.status === 'rejected' && !isNotFound(r.reason)) {
      remaining.push(runIds[i]);
      firstError = firstError ?? r.reason;
    }
  });
  return { remaining, firstError };
}

/**
 * Delete control-testing runs — pass every attempt of a rerun chain to delete the whole row, as the
 * control's Test Runs tab does. Resolves with the runs that could not be deleted (and shows one
 * error for them). Refreshes the Test Runs page and every control's Test Runs tab either way.
 */
export function useDeleteControlTestRun(): UseMutationResult<DeleteRunsResult, Error, string[]> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runIds: string[]) => {
      const { remaining, firstError } = await deleteRuns(runIds);
      if (remaining.length > 0) {
        // The interceptor already shows module-access denials (once per burst): nothing to add.
        if (!isAccessDenied(firstError)) {
          const msg = resolve403ErrorMessage(firstError, getApiErrorMessage(firstError, 'Failed to delete test run'));
          const prefix = remaining.length < runIds.length
            ? `${remaining.length} of ${runIds.length} attempts were not deleted: `
            : '';
          // Reuse the interceptor's toast id when it showed one, so this message replaces it rather
          // than stacking a second toast.
          toast.error(prefix + msg, { id: interceptorToastId(firstError) ?? prefix + msg });
        }
      } else {
        toast.success('Test run deleted');
      }
      return { remaining };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.controlTesting.all, 'test-runs'] });
    },
  });
}
